/**
 * Centralized Single Source of Truth for Form Field User-Facing Display Labels.
 * Used across Pending Requirements, Inline Error Validations, and Tooltips.
 */

export const FIELD_DISPLAY_LABELS: Record<string, string> = {
  // Shipper & Consignee
  shipperName: 'Shipper Name',
  shipperCompany: 'Shipper Company',
  shipperAddressLine1: 'Shipper Address',
  shipperCity: 'Shipper City',
  shipperCountry: 'Shipper Country',
  shipperPincode: 'Shipper Pincode',
  shipperContactNumber: 'Shipper Phone Number',
  consigneeName: 'Consignee Name',
  consigneeCompany: 'Consignee Company',
  consigneeAddressLine1: 'Consignee Address',
  consigneeCity: 'Consignee City',
  consigneeCountry: 'Consignee Country',
  consigneePincode: 'Consignee Pincode',
  consigneeContactNumber: 'Consignee Phone Number',
  consigneeAltContactNumber: 'Consignee Alternate Mobile No.',
  consigneeAltContactTelephoneCode: 'Consignee Alternate Country Code',

  // Shipment Specifications
  unitOfMeasurement: 'Unit of Measurement',
  currency: 'Currency',
  shipmentMode: 'Shipment Mode',
  incoterm: 'Incoterm',
  freight: 'Freight',
  freightInsurance: 'Insurance',
  iossNumber: 'IOSS Number',
  iorEoriNumber: 'IOR / EORI No.',
  recipientVatTaxId: 'Recipient VAT / Tax ID',
  purposeOfShipment: 'Purpose Of Shipment',
  dutyCharge: 'Who pays duty & taxes? (DDP / DDU)',

  // Box Dimensions & Weights
  boxes: 'Boxes',
  length: 'Length',
  width: 'Width',
  height: 'Height',
  weight: 'Weight',

  // Declared Goods Base Fields
  products: 'Declared Goods',
  description: 'Description',
  quantity: 'Quantity',
  unitValue: 'Unit Value',
  HSNCode: 'HSN Code',
  HSNName: 'HSN Name',

  // EU Compliance Fields
  productId: 'Product ID',
  productIdType: 'Product ID Type',
  merchantProductId: 'Merchant ID (M-PID)',
  standardManufacturerProductId: 'Barcode (S-PID)',
  nonStandardManufacturerProductId: 'Manufacturer part no. (NS-PID)',

  // US CPSC Compliance Fields
  cpscBeingSold: 'Does it need a US safety certificate (GCC or CPC)?',
  cpscDisclaimCode: 'Disclaim Code',
  cpscIntendedUseCode: 'Intended Use Code',
  cpscAdditionalContext: 'Additional Context',
  cpscCertificateFiled: 'Certificate filed in CPSC Registry',
  cpscProductIdRegistry: 'Product ID (registry)',
  cpscCertificateVersionId: 'Certificate Version ID',
  cpscCertifierId: 'Certifier ID',
  cpscProductId: 'Product ID',
  cpscProductIdType: 'Product ID type',
};

/**
 * Returns a human-readable requirement label with exact row/box context.
 * e.g. "Box 2, Declared Goods Row 3 — Merchant ID (M-PID)" or "Recipient VAT / Tax ID"
 */
export function formatPendingRequirementLabel(
  fieldKey: string,
  context?: {
    boxNumber?: number;
    productRowNumber?: number;
    customPrefix?: string;
  },
): string {
  const displayLabel = FIELD_DISPLAY_LABELS[fieldKey] || fieldKey;

  const parts: string[] = [];
  if (context?.customPrefix) {
    parts.push(context.customPrefix);
  } else {
    if (context?.boxNumber !== undefined) {
      if (context?.productRowNumber !== undefined) {
        parts.push(`Box ${context.boxNumber}, Declared Goods Row ${context.productRowNumber}`);
      } else {
        parts.push(`Box ${context.boxNumber}`);
      }
    } else if (context?.productRowNumber !== undefined) {
      parts.push(`Declared Goods Row ${context.productRowNumber}`);
    }
  }

  if (parts.length > 0) {
    return `${parts.join(' — ')} — ${displayLabel}`;
  }

  return displayLabel;
}
